<script setup>
// @ts-check

import { computed } from 'vue'
import { RouterLink } from 'vue-router'

const props = defineProps({
  heading: {
    type: String,
    required: true
  },
  image: {
    type: String,
    required: true
  },
  imageClass: {
    type: String,
    default: null
  },
  imagePosition: {
    type: String,
    default: 'left'
  },
  action: {
    type: String,
    default: 'link',
    /** @param {string} action */
    validator: (action) => ['link', 'event', 'router'].includes(action)
  },
  href: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['action'])

if (props.action === 'link' && props.href === null) {
  throw new Error(`Href is required for action: ${props.action}`)
}

if (props.action === 'router') {
  throw new Error(`Not implemented for action: ${props.action}`)
}

const clickableElement = computed(() => {
  switch (props.action) {
    case 'link':
      return 'a'
    case 'event':
      return 'button'
    case 'router':
      return RouterLink
    default:
      throw new Error(`Unknown type: ${props.action}`)
  }
})

const dynamicAttributes = computed(() => {
  if (props.action === 'event') {
    return {
      type: 'button'
    }
  }

  if (props.action === 'link') {
    return { href: props.href, target: '_blank' }
  }

  return {}
})
</script>

<template>
  <div
    class="flex flex-col items-center justify-center gap-y-4 p-4 sm:flex-row sm:gap-x-8 sm:gap-y-0 md:px-12 md:py-2"
  >
    <img
      v-if="imagePosition === 'left'"
      class="h-auto max-w-48"
      :class="imageClass"
      :alt="heading"
      :src="image"
    />
    <div class="mt-4 flex flex-col items-start justify-center gap-y-4">
      <slot name="content-top" />
      <h2 class="text-lg font-medium">
        {{ heading }}
      </h2>
      <slot name="default" />

      <component
        v-bind="dynamicAttributes"
        :is="clickableElement"
        class="inline-block text-left font-secondary font-semibold text-primary no-underline"
        @click="emit('action')"
      >
        <slot name="action" />
        <span v-if="action === 'event'"> › </span>
      </component>
    </div>
    <img
      v-if="imagePosition === 'right'"
      class="h-auto max-w-48"
      :class="imageClass"
      :alt="heading"
      :src="image"
    />
  </div>
</template>
